/** @format */

import { axiosInstance } from '@common/application/context/Providers';
import { Resource } from '@common/application/enums/Resource';
import { ResourceUrl } from '@common/application/helpers/generateSignedUrlsForResults';
import uploadFileToSignedUrl from '@common/application/helpers/uploadFileToSignedUrl';
import { ElasticResponse, ElasticSearchAfter } from '@common/types/elastic';
import { Nullable } from '@common/types/utils';

export class CompanyBrand {
  id: number;
  companyId: number;
  brand: string;
  logo: ResourceUrl;
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;

  constructor(companyId: number, brand: string, logo: ResourceUrl) {
    this.companyId = companyId;
    this.brand = brand;
    this.logo = logo;
  }

  static list(query, sort, queryOpts?: Record<string, unknown>, size?: number, pit?: Nullable<string>, search_after?: Nullable<ElasticSearchAfter>): ElasticResponse<CompanyBrand> {
    // Ensure this code is not ran by SSR!
    if (typeof window !== 'undefined') {
      return axiosInstance
        .get(`/api/v1/company/brands`, {
          params: {
            sequence_id: 1,
            pit,
            query,
            queryOpts: JSON.stringify(queryOpts ?? { deleted: false }),
            sort: JSON.stringify(sort),
            search_after: JSON.stringify(search_after),
            size,
          },
        })
        .then((res) => ({ ...res.data }));
    }
    throw new Error();
  }

  static delete(id: number): Promise<Record<string, unknown>> {
    return axiosInstance.delete(`/api/v1/company/brands/${id}`).then((res) => ({ ...res.data }));
  }

  static undelete(id: number): Promise<Record<string, unknown>> {
    return axiosInstance.post(`/api/v1/company/brands/${id}/undelete`).then((res) => ({ ...res.data }));
  }

  static update(updatedCompany: CompanyBrand, id: number): Promise<Record<string, unknown>> {
    return axiosInstance.patch(`/api/v1/company/brands/${id}`, { payload: updatedCompany }).then(async (res) => {
      const { signedUrl } = res.data.payload;
      if (signedUrl) {
        await uploadFileToSignedUrl(signedUrl, updatedCompany.logo.url as File, res.data.payload, 'logo', Resource.COMPANY_BRAND);
      }
      return { ...res.data };
    });
  }

  add(fileName: string, fileExt: string): Promise<Record<string, unknown>> {
    return axiosInstance
      .post(`/api/v1/company/brands`, {
        payload: {
          brand: this.brand,
          companyId: this.companyId,
          logo: { ext: fileExt, name: fileName, dataPresent: !!this.logo.url },
        },
      })
      .then(async (res) => {
        const { signedUrl } = res.data.payload;
        await uploadFileToSignedUrl(signedUrl, this.logo.url as File, res.data.payload, 'logo', Resource.COMPANY_BRAND);
        return { ...res.data };
      });
  }
}
